import dayjs from "dayjs";
import { UserBookingInterest } from "src/models/BookingInterest";

/**
 * The API's refusal when a ticket cannot be issued because nobody has verified a mobile
 * number for it. Mirrors BookingInterestHelper.PhoneNumberNotConfirmedError on the backend,
 * which is matched on rather than parsed — keep the two identical.
 */
export const PHONE_NUMBER_REQUIRED_ERROR =
  "We need a verified mobile number before we can send this ticket. Please use the link in your invitation email to add and verify your number.";

/**
 * Whether this booking is accepted through the allocation mini-flow
 * (`/attendance-allocations`) rather than the general offer page (`/audience/ticket/accept`).
 *
 * Mirrors BookingInterestHelper.IsAcceptedThroughAllocationFlow. The two pages are not
 * interchangeable: only the mini-flow collects and verifies a mobile number, which is why a
 * VIP who reached the general page could accept without one. Priority is served from the
 * queue like General Admission and accepts on the general page.
 */
export const isAcceptedThroughAllocationFlow = (
  bookingInterest?: UserBookingInterest
): boolean => {
  // Not every endpoint sends both halves: the offer pages read a DTO
  // (GetBookingInterestByIdToken) that carries only the nested allocationSession, while the
  // profile's carries allocationSessionId too. Testing the id alone made this silently false
  // on the two pages it was written for. Either one proves the booking sits in an allocation.
  const isAllocationBooking =
    !!bookingInterest?.allocationSession || !!bookingInterest?.allocationSessionId;

  // An allocation we cannot identify is treated as a mini-flow one: that page asks for a
  // verified number and the general offer page does not, so it is the safe way to be wrong.
  return isAllocationBooking && bookingInterest?.allocationSession?.allocation?.type !== "Priority";
};

/** Where this booking's Accept / Decline link must point. */
export const ticketActionPath = (
  bookingInterest: UserBookingInterest | undefined,
  token: string,
  action: "accept" | "decline"
): string =>
  isAcceptedThroughAllocationFlow(bookingInterest)
    ? `/attendance-allocations?token=${token}`
    : `/audience/ticket/${action}?token=${token}`;

/**
 * The same mini-flow, reached from inside the app rather than from an emailed link.
 *
 * Takes the booking id instead of an offer token because the person is already signed in:
 * the page, the phone verification steps and the accept command all have an id-based
 * counterpart, so nothing here needs a token — and a profile URL is the last place to put
 * one.
 */
export const allocationFlowPathById = (bookingInterestId: number): string =>
  `/attendance-allocations?bookingInterestId=${bookingInterestId}`;

export const validationBookingInterestsFilter = (
  relatedInterest: UserBookingInterest
): boolean => {
  return (
    (relatedInterest.status === "TicketAccepted" ||
    relatedInterest.status === "AttendanceConfirmedViaSMS" ||
    relatedInterest.status === "AttendanceSMSReminderSent" ||
    relatedInterest.status === "InvitedToTicket") &&
    relatedInterest.isDependent === true
  );
};

export const copyToClipboard = ((text: string) => {
  navigator.clipboard.writeText(text);
});

export const handleAutoScrolling = (id: string) => {
  const section = document.getElementById(id);
  if (section) {
    section.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
  }
};

export type BookingFilter = "application" | "offer" | "ticket" | "past";

export const bookingFilters: { value: BookingFilter; label: string }[] = [
  { value: "application", label: "Application" },
  { value: "offer", label: "Ticket Offer" },
  { value: "ticket", label: "Ticket" },
  { value: "past", label: "Past Show" },
];

export const isBookingFilter = (value: unknown): value is BookingFilter =>
  bookingFilters.some((filter) => filter.value === value);

export const isSessionPast = (session?: { approxFinishTime?: string }): boolean =>
  dayjs(session?.approxFinishTime).isBefore(dayjs());

export const isPastBooking = (bookingInterest: UserBookingInterest): boolean =>
  isSessionPast(bookingInterest.session) ||
  bookingInterest.status === "EnteredSession" ||
  bookingInterest.status === "UnattendedSession";

export const getBookingFilter = (bookingInterest: UserBookingInterest): BookingFilter | null => {
  if (isPastBooking(bookingInterest)) {
    return "past";
  }

  switch (bookingInterest.status) {
    case "InterestRegistered":
    case "RejoinedQueue":
    case "JoinedLastMinQueue":
    case "InterestDeclined":
      return "application";
    case "InvitedToTicket":
    case "TicketTimedOut":
    case "SecondChanceExpired":
      return "offer";
    case "TicketAccepted":
    case "AttendanceSMSReminderSent":
    case "AttendanceConfirmedViaSMS":
    case "AttendanceDeclinedViaSMS":
    case "AttendanceConfirmationTimedOutViaSMS":
    case "TurnedAway":
      return "ticket";
    case "TicketDeclined":
    case "TicketOrInterestRevoked":
      return getFunnelStageReached(bookingInterest);
    default:
      return null;
  }
};

const getFunnelStageReached = (bookingInterest: UserBookingInterest): BookingFilter => {
  if (bookingInterest.ticket) {
    return "ticket";
  }
  if (bookingInterest.invitedToTicketExpiration) {
    return "offer";
  }
  return "application";
};

export const canRejoinBooking = (bookingInterest: UserBookingInterest): boolean =>
  (bookingInterest.status === "TicketTimedOut" ||
    bookingInterest.status === "AttendanceConfirmationTimedOutViaSMS") &&
  !!bookingInterest.session?.consolidatedFlowEnabled &&
  !bookingInterest.hasUsedSecondChance &&
  !bookingInterest.isDependent &&
  !isSessionPast(bookingInterest.session);

const getUpcomingSortGroup = (bookingInterest: UserBookingInterest): number => {
  switch (bookingInterest.status) {
    case "InvitedToTicket":
    case "TicketAccepted":
    case "AttendanceSMSReminderSent":
    case "AttendanceConfirmedViaSMS":
      return 0;
  }
  if (canRejoinBooking(bookingInterest)) {
    return 0;
  }
  switch (bookingInterest.status) {
    case "InterestRegistered":
    case "RejoinedQueue":
    case "JoinedLastMinQueue":
      return 1;
    default:
      return 2;
  }
};

export const filterAndSortBookingInterests = (
  bookingInterests: UserBookingInterest[],
  filter: BookingFilter | null
): UserBookingInterest[] => {
  const arrivalTime = (bookingInterest: UserBookingInterest) =>
    dayjs(bookingInterest.session?.arrivalTime).valueOf() || 0;

  if (filter === "past") {
    return bookingInterests
      .filter(isPastBooking)
      .sort((a, b) => arrivalTime(b) - arrivalTime(a));
  }

  return bookingInterests
    .filter((bookingInterest) =>
      filter ? getBookingFilter(bookingInterest) === filter : !isPastBooking(bookingInterest)
    )
    .sort((a, b) =>
      getUpcomingSortGroup(a) - getUpcomingSortGroup(b) || arrivalTime(a) - arrivalTime(b)
    );
};

const bookingHolderName = (bookingInterest: UserBookingInterest): string =>
  `${bookingInterest.assignedFirstName ?? bookingInterest.user?.firstName ?? ""} ${bookingInterest.assignedLastName ?? bookingInterest.user?.lastName ?? ""}`;

const normaliseSearchText = (text: string): string =>
  text.toLowerCase().replace(/\s+/g, " ").trim();

export const matchesBookingSearch = (bookingInterest: UserBookingInterest, searchTerm: string): boolean => {
  const term = normaliseSearchText(searchTerm);
  if (!term) {
    return true;
  }

  const searchableTexts = [
    bookingInterest.session?.show?.name,
    bookingInterest.session?.name,
    bookingHolderName(bookingInterest),
    ...(bookingInterest.relatedBookingInterests ?? []).map(bookingHolderName),
    ...(bookingInterest.parentAndSiblingInterests ?? []).map(bookingHolderName),
  ];

  return searchableTexts.some((text) => !!text && normaliseSearchText(text).includes(term));
};
